import { useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { getDownloadURL, getMetadata, ref } from 'firebase/storage';

import { storage } from '../../firebase-config.js';
import { type FileUploadType, STORAGE_PATH_MAP } from '../../features/file-upload';
import { getDescription } from './utils';
import type { DocumentFromStorage } from './DocumentManager.types.ts';

type UseGetDocumentProps = {
  documentId: string;
  patientId: string;
  type: FileUploadType;
};

const getDocument = async ({ documentId, patientId, type }: UseGetDocumentProps) => {
  const storagePath = STORAGE_PATH_MAP[type]?.({ patientId });

  if (!storagePath) {
    throw new Error('Storage path not found');
  }

  const docRef = ref(storage, `${storagePath}/${documentId}`);

  const url = await getDownloadURL(docRef);

  if (!url) {
    return null;
  }

  const metadata = await getMetadata(docRef);

  const description = getDescription({
    metadata,
    fileName: docRef.name,
  });

  const fileName = metadata?.customMetadata?.fileName || docRef.name;
  const id = metadata?.customMetadata?.fileUploadDocId || docRef.fullPath;

  return {
    id,
    url,
    fileName,
    description,
    fileSize: metadata?.size,
    fullPath: docRef.fullPath,
    metadata: metadata?.customMetadata,
    uploadedAt: metadata?.timeCreated ? new Date(metadata.timeCreated) : null,
    type,
  } satisfies DocumentFromStorage;
};
/**
 * @param documentId - The id of the document to get
 * @param patientId - The id of the patient to get the document for
 * @param type - The type of document to get
 * @returns The document and a loading state
 */
export const useGetDocument = ({ documentId, patientId, type }: UseGetDocumentProps) => {
  const [document, setDocument] = useState<DocumentFromStorage | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!patientId) {
      return;
    }

    (async () => {
      try {
        setIsLoading(true);
        setIsError(false);

        const document = await getDocument({ documentId, patientId, type });
        setDocument(document);
      } catch (error) {
        captureException(error, { tags: { issueIn: 'useGetDocument' }, extra: { documentId, patientId, type } });
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    })();

    return () => {
      setDocument(null);
    };
  }, [documentId, patientId, type]);

  return { document, isLoading, isError };
};
